import { deepEqual } from "fast-equals"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ITEM_TYPE } from "store/apis/storeAPI/values"

export const SHOP_TYPE = {
  COMMON: "common",
  TON: "ton"
} as const

export type IShopType = (typeof SHOP_TYPE)[keyof typeof SHOP_TYPE]

interface StoreFiltersState {
  itemType: ITEM_TYPE
  shopType: IShopType
  appliedFilters: Record<string, any>
  draftFilters: Record<string, any>
  sortParams: Record<string, any>
  showOnlyFavorites: boolean
}

const initialState: StoreFiltersState = {
  shopType: SHOP_TYPE.COMMON,
  itemType: ITEM_TYPE.SHOVEL,
  appliedFilters: { type: ITEM_TYPE.SHOVEL },
  draftFilters: { type: ITEM_TYPE.SHOVEL },
  sortParams: { createdAt: -1 },
  showOnlyFavorites: false
}

const storeFiltersSlice = createSlice({
  name: "storeFilters",
  initialState,
  reducers: {
    setShopType(state, action: PayloadAction<IShopType>) {
      state.shopType = action.payload
    },
    setItemType(state, action: PayloadAction<ITEM_TYPE>) {
      state.itemType = action.payload
      state.appliedFilters = { type: action.payload }
      state.draftFilters = { type: action.payload }
      state.sortParams = { createdAt: -1 }
    },

    setDraftFilters(state, action: PayloadAction<Record<string, any>>) {
      state.draftFilters = action.payload
    },

    addDraftFilters(state, action: PayloadAction<Record<string, any>>) {
      Object.entries(action.payload).forEach(([key, value]) => {
        state.draftFilters[key] = value
      })
    },

    applyFilters(state) {
      state.appliedFilters = { ...state.draftFilters }
    },

    setSortParams(state, action: PayloadAction<Record<string, any>>) {
      state.sortParams = action.payload
    },

    resetFilters(state) {
      if (
        deepEqual(state.appliedFilters, state.draftFilters) &&
        deepEqual(state.appliedFilters, { type: state.itemType })
      ) {
        return
      }
      state.appliedFilters = { type: state.itemType }
      state.draftFilters = { type: state.itemType }
    },

    resetSortParams(state) {
      state.sortParams = { createdAt: -1 }
    },

    toggleShowOnlyFavorites(state) {
      state.showOnlyFavorites = !state.showOnlyFavorites
    }
  }
})

export const {
  setShopType,
  setItemType,
  setDraftFilters,
  addDraftFilters,
  applyFilters,
  setSortParams,
  resetFilters,
  resetSortParams,
  toggleShowOnlyFavorites
} = storeFiltersSlice.actions

export default storeFiltersSlice
